import {
  TableRow,
  TableCell,
  Checkbox,
  Typography,
  Box,
  Chip,
  Tooltip,
  IconButton,
  Card,
  CardMedia,
} from "@mui/material";
import { Assignment, OpenInNew, Person } from "@mui/icons-material";
import { AuthorizationJob } from "./authorization-types";
import {
  User,
  JobStatus,
  colorByStatus,
  statusToLabel,
} from "../../pages/playground/types";

/**
 * Props for the JobAuthorizationBatchItem component
 */
type JobAuthorizationBatchItemProps = {
  job: AuthorizationJob & {
    garment_type?: string;
  };
  isSelected: boolean;
  copywriters: User[];
  onJobClick: (id: string) => void;
  onAssignButtonClick: (id: string) => void;
  onUnassignButtonClick: (id: string) => void;
};

/**
 * Generates a unique color based on a string (user ID)
 * Uses a deterministic hash function to ensure the same user always gets the same color
 * Returns an object with background and text colors suitable for the UI
 */
const generateUserColor = (userId: string) => {
  // Simple hash function to convert string to a number
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash << 5) - hash + userId.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }

  // Convert hash to hex color, ensuring it's not too light (for visibility)
  // Use the hash to generate hue (0-360), fixed saturation and lightness for good contrast
  const hue = Math.abs(hash % 360);

  // Return both background and text color
  return {
    background: `hsl(${hue}, 70%, 88%)`,
    border: `hsl(${hue}, 70%, 60%)`,
    text: `hsl(${hue}, 80%, 25%)`, // Darker text for good contrast
  };
};

/**
 * Formats user display information including name and surname
 */
const formatUserDisplay = (user: User) => {
  const nameDisplay =
    [user.name, user.surname].filter(Boolean).join(" ") ||
    user.email.split("@")[0];
  return nameDisplay;
};

/**
 * Get readable label for assignment function (editor/reviewer)
 */
const getAssignmentFunctionLabel = (functionType: string): string => {
  const functionMap: Record<string, string> = {
    editor: "Copywriter",
    reviewer: "Reviewer",
  };
  return functionMap[functionType.toLowerCase()] || functionType;
};

/**
 * Renders a single job item within the authorization batch list
 * This component is used in both grouped and ungrouped views
 */
export const JobAuthorizationBatchItem = ({
  job,
  isSelected,
  copywriters,
  onJobClick,
  onAssignButtonClick,
  onUnassignButtonClick,
}: JobAuthorizationBatchItemProps) => {
  return (
    <TableRow
      hover
      role="checkbox"
      aria-checked={isSelected}
      tabIndex={-1}
      key={job.id}
      selected={isSelected}
      onClick={() => onJobClick(job.id)}
      sx={{
        cursor: "pointer",
        "&.MuiTableRow-root.Mui-selected": {
          backgroundColor: "rgba(25, 118, 210, 0.12)",
        },
        "&.MuiTableRow-root.Mui-selected:hover": {
          backgroundColor: "rgba(25, 118, 210, 0.16)",
        },
      }}
    >
      <TableCell padding="checkbox">
        <Checkbox checked={isSelected} />
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          component="a"
          href={`/job/${job.id}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.stopPropagation();
          }}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            "&:hover": {
              textDecoration: "underline",
              cursor: "pointer",
            },
          }}
        >
          {job.sku}
          <OpenInNew
            sx={{
              fontSize: 14,
              ml: 0.5,
              verticalAlign: "middle",
            }}
          />
        </Typography>
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex" }}>
          {job.thumbnails && job.thumbnails.length > 0 ? (
            job.thumbnails.slice(0, 6).map((thumb, idx) => (
              <Card
                key={idx}
                sx={{
                  width: 50,
                  height: 50,
                  boxShadow: "none",
                }}
              >
                <CardMedia
                  component="img"
                  height="50"
                  image={thumb}
                  sx={{ objectFit: "contain" }}
                  alt={`${job.sku} thumbnail ${idx + 1}`}
                />
              </Card>
            ))
          ) : (
            <Box
              sx={{
                width: 50,
                height: 50,
                bgcolor: "grey.200",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                No image
              </Typography>
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell>
        {job.garment_type ? (
          <Typography variant="body2">{job.garment_type}</Typography>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            Not generated
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={statusToLabel(job.status as JobStatus)}
          size="small"
          variant="outlined"
          sx={{
            borderColor: colorByStatus[job.status as JobStatus] || "#ccc",
            color: colorByStatus[job.status as JobStatus] || "#ccc",
            fontWeight: "500",
          }}
        />
      </TableCell>
      <TableCell>
        {job.is_assigned ? (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {job.assignments.map((assignment) => {
              const copywriter = copywriters.find(
                (c) => c.id === assignment.user_id
              );

              return (
                <>
                  {copywriter ? (
                    <Chip
                      label={`${formatUserDisplay(
                        copywriter
                      )} · ${getAssignmentFunctionLabel(assignment.function)} `}
                      size="small"
                      variant="outlined"
                      sx={{
                        maxWidth: "unset", // Remove max-width limitation
                        height: "auto", // Allow multiple lines
                        whiteSpace: "normal", // Allow text wrapping
                        borderColor: generateUserColor(
                          copywriter.id || copywriter.email
                        ).border,
                        backgroundColor: generateUserColor(
                          copywriter.id || copywriter.email
                        ).background,
                        color: generateUserColor(
                          copywriter.id || copywriter.email
                        ).text,
                        fontWeight: 500,
                        "& .MuiChip-label": {
                          whiteSpace: "normal", // Allow text wrapping in the label
                          overflow: "visible", // Show overflowing text
                          textOverflow: "clip", // Don't use ellipsis
                          display: "block", // Make it a block to respect line breaks
                          wordBreak: "break-word", // Break long words if needed
                        },
                        "& .MuiChip-deleteIcon": {
                          color: generateUserColor(
                            copywriter.id || copywriter.email
                          ).text,
                          opacity: 0.7,
                          "&:hover": {
                            opacity: 1,
                          },
                        },
                      }}
                      onDelete={(e) => {
                        e.stopPropagation();
                        onUnassignButtonClick(assignment.id);
                      }}
                    />
                  ) : (
                    <Chip
                      icon={<Person fontSize="small" />}
                      label="Unknown user"
                      size="small"
                      variant="outlined"
                      color="default"
                      onDelete={(e) => {
                        e.stopPropagation();
                        onUnassignButtonClick(assignment.id);
                      }}
                    />
                  )}
                </>
              );
            })}
          </Box>
        ) : (
          <Typography variant="caption" color="error">
            Not assigned
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Tooltip title="Assign">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onAssignButtonClick(job.id);
            }}
          >
            <Assignment fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};
